import { Search, Users, RotateCcw, Star, Camera, CheckCircle, Wallet, Handshake } from "lucide-react";
import { motion, useScroll, useTransform } from "framer-motion";
import { useRef, useState } from "react";
import { Card } from "@/components/ui/card";
import { cn } from "@/lib/utils";
import howItWorksStep1 from "@/assets/how-it-works-step1.webp";
import howItWorksStep2 from "@/assets/how-it-works-step2.webp";
import howItWorksStep3 from "@/assets/how-it-works-step3.webp";
import howItWorksStep4 from "@/assets/how-it-works-step4.webp";
import howItWorksListItem from "@/assets/how-it-works-list-item.png";
import howItWorksAccept from "@/assets/how-it-works-accept.png";
import howItWorksGetPaid from "@/assets/how-it-works-get-paid.png";
import howItWorksHandoff from "@/assets/how-it-works-handoff.png";

const renterSteps = [
  {
    icon: Search,
    title: "Find an Item",
    description: "Browse or search items near you — from cameras to camping gear.",
    color: "from-blue-500/20 to-cyan-500/20",
    image: howItWorksStep1
  },
  {
    icon: Users,
    title: "Reserve Instantly",
    description: "Instant Book — no approval needed. Coordinate pickup and pay securely at handoff.",
    color: "from-purple-500/20 to-pink-500/20",
    image: howItWorksStep2
  },
  {
    icon: RotateCcw,
    title: "Pick Up",
    description: "Easy handoff with protected deposits and hassle-free pickup.",
    color: "from-green-500/20 to-emerald-500/20",
    image: howItWorksStep3
  },
  {
    icon: Star,
    title: "Return & Review",
    description: "Return the item and leave a review to help the community.",
    color: "from-amber-500/20 to-yellow-500/20",
    image: howItWorksStep4
  }
];

const listerSteps = [
  {
    icon: Camera,
    title: "List Your Item",
    description: "Add photos, set availability, and choose your pricing.",
    color: "from-orange-500/20 to-amber-500/20",
    image: howItWorksListItem
  },
  {
    icon: CheckCircle,
    title: "Confirm Pickup",
    description: "Bookings come in instantly. Confirm the pickup time and coordinate the handoff.",
    color: "from-indigo-500/20 to-violet-500/20",
    image: howItWorksAccept
  },
  {
    icon: Handshake,
    title: "Hand Off Your Item",
    description: "Meet the renter, confirm the pickup code, and hand over the item with confidence.",
    color: "from-green-500/20 to-emerald-500/20",
    image: howItWorksHandoff
  },
  {
    icon: Wallet,
    title: "Receive & Get Paid",
    description: "Get your item back at the end of the rental and receive secure payment directly to your account.",
    color: "from-teal-500/20 to-cyan-500/20",
    image: howItWorksGetPaid
  }
];

const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.15,
      delayChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 30, scale: 0.9 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.25, 0.1, 0.25, 1] as const
    }
  }
};

interface StepCardProps {
  step: {
    icon: React.ElementType;
    title: string;
    description: string;
    color: string;
    image?: string;
  };
  index: number;
}

const StepCard = ({ step, index }: StepCardProps) => {
  const [isHovered, setIsHovered] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const [mousePos, setMousePos] = useState({ x: 50, y: 50 });

  const handleMouseMove = (e: React.MouseEvent) => {
    if (!cardRef.current) return;
    const rect = cardRef.current.getBoundingClientRect();
    setMousePos({
      x: ((e.clientX - rect.left) / rect.width) * 100,
      y: ((e.clientY - rect.top) / rect.height) * 100,
    });
  };

  return (
    <motion.div 
      variants={itemVariants}
      whileHover={{ 
        scale: 1.05,
        rotateY: index === 0 ? 5 : index === 2 ? -5 : 0,
        z: 50
      }}
      transition={{ type: "spring", stiffness: 300, damping: 20 }}
      style={{ transformStyle: "preserve-3d" }}
    >
      <div
        ref={cardRef}
        className="relative group h-full"
        onMouseMove={handleMouseMove}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
      >
        {/* Glowing border effect */}
        <div 
          className="absolute -inset-px rounded-2xl opacity-0 group-hover:opacity-100 transition-all duration-500 blur-sm"
          style={{
            background: `linear-gradient(135deg, hsl(var(--primary) / 0.4), hsl(var(--accent) / 0.3), hsl(var(--primary) / 0.4))`,
          }}
        />
        
        <Card 
          className={cn("relative text-center bg-card border-border/40 rounded-2xl h-full overflow-hidden transition-all duration-300 group-hover:shadow-3d-hover", step.image ? "p-0" : "p-8")}
        >
          {/* Spotlight effect */}
          <div 
            className="absolute inset-0 opacity-0 group-hover:opacity-100 transition-opacity duration-300 pointer-events-none"
            style={{
              background: `radial-gradient(circle at ${mousePos.x}% ${mousePos.y}%, hsl(var(--primary) / 0.12), transparent 50%)`
            }}
          />
          
          {/* Gradient background */}
          <div className={`absolute inset-0 bg-gradient-to-br ${step.color} opacity-0 group-hover:opacity-100 transition-opacity duration-500`} />
          
          <div className="relative z-10">
            {step.image ? (
              <>
                <div className="relative">
                  <img src={step.image} alt={step.title} width={600} height={800} className="w-full aspect-[3/4] object-cover rounded-t-2xl" />
                  <span className="absolute top-3 left-3 inline-flex items-center justify-center w-8 h-8 rounded-full bg-gradient-to-br from-pink-100 via-pink-50 to-purple-100 text-slate-900 border border-border/40 text-sm font-bold shadow-md">
                    {index + 1}
                  </span>
                </div>
                <div className="p-4 text-center">
                  <h3 className="text-lg font-bold mb-1 text-foreground group-hover:text-primary transition-colors duration-300">
                    {step.title}
                  </h3>
                  <p className="text-sm text-muted-foreground leading-relaxed">{step.description}</p>
                </div>
              </>
            ) : (
              <>
                {/* Icon with 3D lift and glow */}
                <motion.div 
                  className="mb-6 flex justify-center"
                  whileHover={{ y: -5 }}
                >
                  <motion.div 
                    className="relative w-16 h-16 rounded-2xl bg-primary/10 flex items-center justify-center group-hover:bg-primary/20 transition-all duration-300"
                    whileHover={{ 
                      scale: 1.1,
                      boxShadow: "0 15px 40px -15px hsl(var(--primary) / 0.5)"
                    }}
                    animate={isHovered ? { 
                      boxShadow: [
                        "0 0 20px hsl(var(--primary) / 0.2)",
                        "0 0 30px hsl(var(--primary) / 0.3)",
                        "0 0 20px hsl(var(--primary) / 0.2)"
                      ]
                    } : {}}
                    transition={{ duration: 1.5, repeat: Infinity }}
                  >
                    <step.icon className="w-8 h-8 text-primary" />
                  </motion.div>
                </motion.div>
                
                {/* Step number with gradient */}
                <motion.div 
                  className="text-sm font-medium text-muted-foreground mb-2 group-hover:text-primary transition-colors duration-300"
                  whileHover={{ scale: 1.05 }}
                >
                  <span className="inline-flex items-center justify-center w-6 h-6 rounded-full bg-primary/10 text-primary text-xs font-bold mr-2 group-hover:bg-primary group-hover:text-primary-foreground transition-all duration-300">
                    {index + 1}
                  </span>
                  Step {index + 1}
                </motion.div>
                
                <h3 className="text-xl font-bold mb-3 text-foreground group-hover:text-primary transition-colors duration-300">
                  {step.title}
                </h3>
                <p className="text-muted-foreground leading-relaxed">{step.description}</p>
              </>
            )}
          </div>
        </Card>
      </div>
    </motion.div>
  );
};

const HowItWorks = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const [activeTab, setActiveTab] = useState("renters");
  
  const { scrollYProgress } = useScroll({
    target: sectionRef,
    offset: ["start end", "end start"]
  });

  const contentY = useTransform(scrollYProgress, [0, 1], [0, 30]);
  const opacity = useTransform(scrollYProgress, [0, 0.15, 0.55, 0.85], [0, 1, 1, 0]);

  const currentSteps = activeTab === "renters" ? renterSteps : listerSteps;

  return (
    <section ref={sectionRef} className="relative py-24 overflow-hidden" style={{ background: "linear-gradient(to bottom, #ffffff 0%, #DCE6FB 15%, #B6CBF8 50%, #DCE6FB 85%, #ffffff 100%)" }}>
      {/* Floating orbs */}
      <div className="absolute top-32 left-16 w-80 h-80 rounded-full bg-primary/8 blur-3xl floating-orb" />
      <div className="absolute bottom-20 right-16 w-72 h-72 rounded-full bg-accent/10 blur-3xl floating-orb stagger-2" />
      
      <motion.div style={{ y: contentY, opacity }} className="container px-4 sm:px-6 relative z-10">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: false, margin: "-100px" }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <h2 className="text-4xl md:text-5xl font-bold mb-6 text-slate-900">
            How Rentify Works
          </h2>
          
          <div className="inline-flex bg-muted/50 p-1.5 rounded-full shadow-soft backdrop-blur-sm" role="tablist" aria-label="User type selection">
            <button
              type="button"
              role="tab"
              id="howitworks-tab-renters"
              aria-selected={activeTab === "renters"}
              onClick={() => setActiveTab("renters")}
              className={cn(
                "rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300",
                activeTab === "renters" 
                  ? "bg-gradient-to-br from-pink-100 via-pink-50 to-purple-100 text-slate-900 shadow-md hover:from-pink-200 hover:via-pink-100 hover:to-purple-200" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              For Renters
            </button>
            <button
              type="button"
              role="tab"
              id="howitworks-tab-listers"
              aria-selected={activeTab === "listers"}
              onClick={() => setActiveTab("listers")}
              className={cn(
                "rounded-full px-6 py-2.5 text-sm font-medium transition-all duration-300",
                activeTab === "listers" 
                  ? "bg-gradient-to-br from-pink-100 via-pink-50 to-purple-100 text-slate-900 shadow-md hover:from-pink-200 hover:via-pink-100 hover:to-purple-200" 
                  : "text-muted-foreground hover:text-foreground"
              )}
            >
              For Listers
            </button>
          </div>
        </motion.div>
        
        <motion.div 
          key={activeTab}
          variants={containerVariants}
          initial="hidden"
          animate="visible"
          className={cn(
            "grid gap-8 max-w-6xl mx-auto justify-center",
            activeTab === "renters"
              ? "md:grid-cols-2 lg:grid-cols-4"
              : "md:grid-cols-2 lg:grid-cols-4"
          )}
          style={{ perspective: 1200 }}
        >
          {currentSteps.map((step, index) => (
            <StepCard key={`${activeTab}-${index}`} step={step} index={index} />
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};

export default HowItWorks;